import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { toast } from "react-hot-toast";
import CustomExport from "../../../components/CustomExport/CustomExport";
import {
  useFetchAllShelfVisibilityMutation,
  useUpdateShelfVisibilityStatusMutation,
} from "../../../features/tenant/shelfVisibility/shelfVisibility.Api";

const REJECT_PRESETS = [
  "Image is blurry / unclear",
  "Shelf not properly visible",
  "Incorrect brand product displayed",
  "Duplicate submission",
  "Lighting issue / photo too dark",
];

const ShelfVisibility = () => {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);

  const initialRequest = {
    name: "",
    mobile: "",
    status: "",
    startDate: "",
    endDate: "",
    limit: 10,
    offset: 0,
  };

  const [request, setRequest] = useState(initialRequest);
  const [myList, setMyList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // Modal States
  const [selectedImage, setSelectedImage] = useState(null);
  const [rejectModalRecord, setRejectModalRecord] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedReasonModal, setSelectedReasonModal] = useState(null);

  const [
    fetchAllShelfVisibility,
    { data: listData, isLoading: listIsLoading },
  ] = useFetchAllShelfVisibilityMutation();

  const [
    updateStatus,
    { isLoading: updateIsLoading },
  ] = useUpdateShelfVisibilityStatusMutation();

  const [
    fetchCsvData,
    { data: csvResponse },
  ] = useFetchAllShelfVisibilityMutation();

  const token = sessionStorage.getItem("access_token");
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
  const tenantId = vendorData?.tenant_id;

  const fetchRecords = (overrideRequest) => {
    if (!token || !tenantId) return;

    fetchAllShelfVisibility({
      token,
      tenant_id: tenantId,
      body: overrideRequest || request,
    });
  };

  useEffect(() => {
    fetchRecords();
  }, [limit, offset]);

  useEffect(() => {
    if (
      request.name === "" &&
      request.mobile === "" &&
      request.status === "" &&
      request.startDate === "" &&
      request.endDate === ""
    ) {
      fetchRecords();
    }
  }, [request]);

  useEffect(() => {
    if (listData) {
      const responseBody = listData?.body;
      if (Array.isArray(responseBody)) {
        setMyList(responseBody);
        setTotalRows(responseBody.length);
      } else if (responseBody && Array.isArray(responseBody.list)) {
        setMyList(responseBody.list);
        setTotalRows(responseBody.total || responseBody.list.length);
      } else {
        setMyList([]);
        setTotalRows(0);
      }
    }
  }, [listData]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const updatedRequest = { ...request, offset: 0 };
    setOffset(0);
    setCurrentPage(1);
    setRequest(updatedRequest);
    fetchRecords(updatedRequest);
  };

  const clearFilters = () => {
    setRequest(initialRequest);
    setOffset(0);
    setCurrentPage(1);
    fetchRecords(initialRequest);
  };

  const handlePageChange = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    setOffset(newOffset);
    const updatedRequest = { ...request, offset: newOffset };
    setRequest(updatedRequest);
    setCurrentPage(pageNumber);
    fetchRecords(updatedRequest);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
    const updatedRequest = { ...request, limit: newPerPage, offset: 0 };
    setRequest(updatedRequest);
    setPaginationResetToggle((prev) => !prev);
    fetchRecords(updatedRequest);
  };

  const fetchCsvFunction = () => {
    const modifiedRequest = {
      ...request,
      limit: null,
      offset: null,
    };
    fetchCsvData({
      token,
      tenant_id: tenantId,
      body: modifiedRequest,
    });
  };

  const handleApprove = async (recordId) => {
    try {
      const res = await updateStatus({
        token,
        tenant_id: tenantId,
        data: {
          id: recordId,
          status: "2", // Approved
        },
      }).unwrap();

      if (res?.success) {
        toast.success("Image approved successfully!");
        fetchRecords();
      } else {
        toast.error(res?.message || "Failed to approve image");
      }
    } catch (err) {
      console.error("Approve error:", err);
      toast.error(err?.data?.message || "Error approving image");
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    try {
      const res = await updateStatus({
        token,
        tenant_id: tenantId,
        data: {
          id: rejectModalRecord.id,
          status: "3", // Rejected
          reject_reason: rejectReason.trim(),
        },
      }).unwrap();

      if (res?.success) {
        toast.success("Image rejected successfully!");
        setRejectModalRecord(null);
        setRejectReason("");
        fetchRecords();
      } else {
        toast.error(res?.message || "Failed to reject image");
      }
    } catch (err) {
      console.error("Reject error:", err);
      toast.error(err?.data?.message || "Error rejecting image");
    }
  };

  // Format data for CSV Export using Excel's =IMAGE() formula to embed actual images in Excel cells
  const rawCsvList = Array.isArray(csvResponse?.body)
    ? csvResponse?.body
    : csvResponse?.body?.list || [];

  const formattedCsvData = rawCsvList.map((item) => ({
    ...item,
    status:
      item.status === "1"
        ? "Pending"
        : item.status === "2"
        ? "Approved"
        : item.status === "3"
        ? "Rejected"
        : item.status,
    image_url: item.image_url
      ? `=IMAGE("${item.image_url}")`
      : "",
  }));

  const columns = [
    {
      name: "S.No",
      cell: (row, idx) => offset + Number(idx + 1),
      sortable: true,
      width: "60px",
    },
    {
      name: "Name",
      cell: (row) => <span className="font-semibold text-gray-800">{row.user_name || "-"}</span>,
      sortable: true,
      width: "130px",
    },
    {
      name: "Mobile",
      cell: (row) => row.user_mobile || "-",
      sortable: true,
      width: "115px",
    },
    {
      name: "User Type",
      cell: (row) => row.user_type_name || "-",
      sortable: true,
      width: "100px",
    },
    {
      name: "Shelf Image",
      cell: (row) => (
        <div
          className="relative cursor-pointer my-1 group overflow-hidden rounded border border-gray-300 shadow-sm"
          style={{ width: "50px", height: "50px" }}
          onClick={() => setSelectedImage(row.image_url)}
        >
          <img
            src={row.image_url}
            alt="Shelf"
            className="w-full h-full object-cover group-hover:scale-110 transition duration-200"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/60x60?text=No+Img";
            }}
          />
        </div>
      ),
      sortable: false,
      width: "80px",
    },
    {
      name: "Remarks",
      cell: (row) => row.remarks ? <span className="italic text-gray-600">"{row.remarks}"</span> : "-",
      sortable: true,
      width: "120px",
    },
    {
      name: "Status",
      cell: (row) => {
        let badgeBg = "bg-yellow-500";
        let text = "Pending";
        if (row.status === "2") {
          badgeBg = "bg-green-600";
          text = "Approved";
        } else if (row.status === "3") {
          badgeBg = "bg-red-600";
          text = "Rejected";
        }

        return (
          <span className={`text-xs px-2.5 py-1 font-bold rounded text-white inline-block whitespace-nowrap ${badgeBg}`}>
            {text}
          </span>
        );
      },
      sortable: true,
      width: "105px",
    },
    {
      name: "Reviewed By",
      cell: (row) => row.reviewed_by_name ? <span className="font-semibold text-gray-700">{row.reviewed_by_name}</span> : <span className="text-gray-400">-</span>,
      sortable: true,
      width: "130px",
    },
    {
      name: "Reject Reason",
      cell: (row) => {
        if (row.status !== "3" || !row.reject_reason) return <span className="text-gray-400">-</span>;

        const isLong = row.reject_reason.length > 20;
        return (
          <div className="flex flex-col text-red-600 font-medium text-xs">
            <span title={row.reject_reason} className="truncate max-w-[130px]">
              {isLong ? `${row.reject_reason.substring(0, 20)}...` : row.reject_reason}
            </span>
            {isLong && (
              <button
                type="button"
                className="text-blue-600 text-left underline font-semibold mt-0.5 hover:text-blue-800"
                onClick={() => setSelectedReasonModal(row.reject_reason)}
              >
                View Full
              </button>
            )}
          </div>
        );
      },
      sortable: true,
      width: "140px",
    },
    {
      name: "Created At",
      cell: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      width: "150px",
    },
    {
      name: "Action",
      cell: (row) => {
        const isReviewed = row.status === "2" || row.status === "3"; // Approved (2) or Rejected (3)

        return (
          <div className="flex items-center space-x-1 py-1 whitespace-nowrap">
            <button
              className={`px-2.5 py-1 text-xs font-semibold rounded text-white transition ${
                isReviewed
                  ? "bg-gray-400 cursor-not-allowed opacity-60"
                  : "bg-green-600 hover:bg-green-700"
              }`}
              disabled={isReviewed || updateIsLoading}
              onClick={() => handleApprove(row.id)}
            >
              {row.status === "2" ? "Approved" : "Approve"}
            </button>
            <button
              className={`px-2.5 py-1 text-xs font-semibold rounded text-white transition ${
                isReviewed
                  ? "bg-gray-400 cursor-not-allowed opacity-60"
                  : "bg-red-600 hover:bg-red-700"
              }`}
              disabled={isReviewed || updateIsLoading}
              onClick={() => {
                setRejectModalRecord(row);
                setRejectReason("");
              }}
            >
              {row.status === "3" ? "Rejected" : "Reject"}
            </button>
          </div>
        );
      },
      sortable: false,
      width: "160px",
    },
  ];

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          {/* Header Title Section */}
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Shelf Visibility Submissions
            <LinksComponent />
          </div>

          <div className="p-3">
            {/* Custom CSV Export */}
            <div className="mb-2">
              <CustomExport
                fetchData={fetchCsvFunction}
                data={formattedCsvData}
                defaultColumnExport={[
                  "user_name",
                  "user_mobile",
                  "user_type_name",
                  "image_url",
                  "remarks",
                  "status",
                  "reject_reason",
                  "reviewed_by_name",
                  "created_at",
                ]}
                filename="shelf_visibility_submissions"
              />
            </div>

            {/* Filter Bar Box */}
            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
              <form onSubmit={handleSearchSubmit}>
                <div className="flex opt_w100 space-x-2 justify-end">
                  {/* Name Filter */}
                  <div className="w-40 h-10">
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                        Name
                      </label>
                      <input
                        type="search"
                        className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                        placeholder="Name..."
                        value={request.name}
                        onChange={(e) =>
                          setRequest((prev) => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  {/* Mobile Filter */}
                  <div className="w-40 h-10">
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                        Mobile
                      </label>
                      <input
                        type="search"
                        className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                        placeholder="Mobile No..."
                        value={request.mobile}
                        onChange={(e) =>
                          setRequest((prev) => ({ ...prev, mobile: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div className="w-40 h-10">
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                        Status
                      </label>
                      <select
                        className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                        value={request.status}
                        onChange={(e) =>
                          setRequest((prev) => ({ ...prev, status: e.target.value }))
                        }
                      >
                        <option value="">All Statuses</option>
                        <option value="1">Pending (1)</option>
                        <option value="2">Approved (2)</option>
                        <option value="3">Rejected (3)</option>
                      </select>
                    </div>
                  </div>

                  {/* From Date Filter */}
                  <div className="w-40">
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                        From Date
                      </label>
                      <input
                        type="date"
                        className="w-full datepd text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                        value={request.startDate}
                        onChange={(e) =>
                          setRequest((prev) => ({ ...prev, startDate: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  {/* End Date Filter */}
                  <div className="w-40">
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="w-full datepd text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                        value={request.endDate}
                        onChange={(e) =>
                          setRequest((prev) => ({ ...prev, endDate: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  {/* Buttons Section */}
                  <div className="flex flex-row gap-2 h-10">
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">&nbsp;</label>
                      <button
                        type="submit"
                        className="py-2 btn btn-outline-primary clear-both float-right pdbtn_all"
                      >
                        Search
                      </button>
                    </div>
                    <div className="relative searchin">
                      <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-outline-primary clear-both float-right pdbtn_all whitespace-nowrap"
                        onClick={clearFilters}
                      >
                        Clear Filter
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Data Table Component */}
            <div className="p-3 w-full bg-white shadow rounded-lg overflow-x-auto">
              <DataTableComponent
                keyField={(row) => row.id}
                columns={columns}
                data={myList}
                loading={listIsLoading}
                totalRows={totalRows}
                handlePerRowsChange={handlePerRowsChange}
                handlePageChange={handlePageChange}
                paginationDefaultPage={currentPage}
                paginationResetDefaultPage={paginationResetToggle}
                striped
                highlightOnHover
                pointerOnHover
                pagination
              />
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full bg-white p-2 rounded-lg shadow-2xl">
            <img
              src={selectedImage}
              alt="Full Preview"
              className="max-h-[80vh] max-w-full object-contain rounded"
            />
            <button
              className="mt-2 w-full py-1.5 bg-gray-800 text-white rounded text-sm font-semibold hover:bg-gray-900"
              onClick={() => setSelectedImage(null)}
            >
              Close Full View
            </button>
          </div>
        </div>
      )}

      {/* View Full Rejection Reason Modal */}
      {selectedReasonModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedReasonModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b pb-2 mb-3">
              <h3 className="font-bold text-base text-red-600">Rejection Reason</h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 text-xl font-bold leading-none"
                onClick={() => setSelectedReasonModal(null)}
              >
                &times;
              </button>
            </div>
            <p className="text-sm text-gray-800 bg-red-50 border border-red-200 p-3 rounded leading-relaxed">
              {selectedReasonModal}
            </p>
          </div>
        </div>
      )}

      {/* Rejection Submission Modal */}
      {rejectModalRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-red-600 text-white px-4 py-3 flex justify-between items-center">
              <h3 className="font-bold text-lg">Reject Image Submission</h3>
              <button
                className="text-white hover:text-gray-200 text-xl font-bold"
                onClick={() => setRejectModalRecord(null)}
              >
                &times;
              </button>
            </div>

            <div className="p-4">
              <p className="text-xs text-gray-600 mb-3">
                Rejecting submission ID #<b>{rejectModalRecord.id}</b> by <b>{rejectModalRecord.user_name}</b> ({rejectModalRecord.user_mobile}).
              </p>

              {/* Quick Presets */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Quick Reason Presets:
                </label>
                <div className="flex flex-wrap gap-1">
                  {REJECT_PRESETS.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="text-xs px-2 py-1 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition"
                      onClick={() => setRejectReason(preset)}
                    >
                      + {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Reason Input */}
              <div className="mb-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Enter rejection reason..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2 border-t">
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setRejectModalRecord(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-1.5 text-xs font-semibold text-white bg-red-600 rounded hover:bg-red-700"
                disabled={updateIsLoading}
                onClick={handleConfirmReject}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShelfVisibility;
